import { useCallback, useEffect, useRef, useState } from 'react'
import { createNadDebug } from '../utils/debugLog'
import { clampWidth, usePointerResize } from './usePointerResize'
import { MOBILE_VIEWPORT_QUERY } from './useMediaQuery'

const log = createNadDebug('dw:chat-sidebar:resize')
const WIDTH_KEY = 'chat-sidebar-width'
const WIDTH_BOUNDS = { min: 280, max: 1000 }
const DEFAULT_WIDTH = 450
// Desktop starts at 768px; reserve enough room for a usable canvas beside chat.
const MIN_CANVAS_WIDTH = 320

function getBounds(viewportWidth = window.innerWidth) {
  return {
    min: WIDTH_BOUNDS.min,
    max: Math.max(WIDTH_BOUNDS.min, Math.min(WIDTH_BOUNDS.max, viewportWidth - MIN_CANVAS_WIDTH)),
  }
}

function readPreference(): { width: number; source: 'stored' | 'default' } {
  try {
    const stored = localStorage.getItem(WIDTH_KEY)
    const width = stored === null || stored.trim() === '' ? NaN : Number(stored)
    if (Number.isFinite(width)) return { width: clampWidth(width, WIDTH_BOUNDS), source: 'stored' }
  } catch {
    // Preference storage may be unavailable; resizing remains usable.
  }
  return { width: DEFAULT_WIDTH, source: 'default' }
}

/** Keep desktop intent separate from the width available on the current display. */
export function useChatSidebarResize(mobileMode: boolean) {
  const [initialPreference] = useState(readPreference)
  const [preferredWidth, setPreferredWidth] = useState(initialPreference.width)
  const preferredWidthRef = useRef(preferredWidth)
  preferredWidthRef.current = preferredWidth
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth)
  const bounds = getBounds(viewportWidth)
  const width = clampWidth(preferredWidth, bounds)

  useEffect(() => {
    log({ event: 'width-preference-loaded', preferredWidth: initialPreference.width, source: initialPreference.source })
  }, [initialPreference])

  useEffect(() => {
    const updateViewport = () => setViewportWidth(window.innerWidth)
    updateViewport()
    window.addEventListener('resize', updateViewport)
    return () => window.removeEventListener('resize', updateViewport)
  }, [])

  // Report layout changes, not every pointermove or synthetic resize event.
  // The ref supplies the current preference without replaying this effect during a drag.
  useEffect(() => {
    // matchMedia and resize notifications can reach React in separate turns.
    // Do not describe the intermediate mode as the settled expanded layout.
    const viewportIsMobile = window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
    if (viewportIsMobile !== mobileMode) {
      log({
        event: 'layout-mode-pending',
        viewportWidth,
        currentMode: mobileMode ? 'mobile-fullscreen' : 'desktop',
        pendingMode: viewportIsMobile ? 'mobile-fullscreen' : 'desktop',
        modeSettled: false,
      })
      return
    }
    const preferred = preferredWidthRef.current
    const effectiveWidth = mobileMode ? viewportWidth : clampWidth(preferred, getBounds(viewportWidth))
    log({
      event: 'expanded-width-applied',
      modeSettled: true,
      mode: mobileMode ? 'mobile-fullscreen' : 'desktop',
      viewportWidth,
      preferredWidth: preferred,
      effectiveWidth,
      canvasAvailableWidth: mobileMode ? 0 : viewportWidth - effectiveWidth,
      canvasReserve: mobileMode ? 0 : MIN_CANVAS_WIDTH,
      maxWidth: mobileMode ? viewportWidth : getBounds(viewportWidth).max,
      preferenceSaved: false,
    })
  }, [viewportWidth, mobileMode])

  const persistWidth = useCallback((nextWidth: number, reason: string) => {
    try {
      localStorage.setItem(WIDTH_KEY, String(nextWidth))
      log({ event: 'width-saved', preferredWidth: nextWidth, reason })
    } catch {
      log({ event: 'width-save-unavailable', preferredWidth: nextWidth, reason })
    }
  }, [])

  const { onPointerDown } = usePointerResize({
    width,
    onWidthChange: setPreferredWidth,
    getBounds,
    disabled: mobileMode,
    disabledReason: 'mobile-mode',
    log,
    onResizeEnd: persistWidth,
  })

  return { width, onPointerDown }
}
